/**
 * MobileCustomerFullScreen — tier 2 of the mobile Customers browse: a
 * full-screen push (slide-in) matching the Trioty customer detail. Mirrors
 * MobileProductFullScreen for the outer shell, Web-Animations slide-in, close
 * and safe-area handling.
 *
 * Everything from the desktop CustomerProfilePane is preserved: hero, the
 * insight stats (orders / total spent / avg order / last order), the contact
 * rows, the order history, editable Tags and editable email, and the WhatsApp
 * action. Edits go through the shared useUpdateCustomerMutation (email + tags) —
 * an "Edit" toggle reveals the inline editors so the read view stays clean.
 *
 * DATA HONESTY: the customer API carries no segment/tier, notes, or per-order
 * route, so the design's segment badge and note block are omitted rather than
 * invented; the governorate chip fills the badge slot with a real field. The ⋯
 * / bottom-bar actions expose only backed operations (call · WhatsApp · copy
 * phone · edit email/tags).
 */

import { useState } from "react";
import { createPortal } from "react-dom";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { LoadingSkeleton } from "@a2z/ui";
import { useSlideScreen } from "@/shared/hooks/useSlideScreen";
import { ErrorState } from "@/shared/components/ErrorState";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { OrderStatus, SellerCustomerDetailView } from "@/lib/api/types";
import {
  DISPLAY_STATUS_TONE,
  toDisplayStatus,
} from "@/modules/orders/lib/order-status-display";
import { useSellerCustomerQuery } from "@/modules/customers/services/useSellerCustomerQuery";
import { useUpdateCustomerMutation } from "@/modules/customers/services/useUpdateCustomerMutation";
import { initialsOf, tintFor } from "@/modules/customers/lib/customer-avatar";
import { governorateName } from "./customers-mobile-model";

type Props = {
  storeSlug: string;
  customerId: string | null;
  locale: Locale;
  onClose: () => void;
};

export function MobileCustomerFullScreen({ storeSlug, customerId, locale, onClose }: Props) {
  // Mount only while open; a fresh mount per customer id replays the slide-in.
  if (!customerId) return null;
  return (
    <Screen
      key={customerId}
      storeSlug={storeSlug}
      customerId={customerId}
      locale={locale}
      onClose={onClose}
    />
  );
}

function Screen({
  storeSlug,
  customerId,
  locale,
  onClose,
}: {
  storeSlug: string;
  customerId: string;
  locale: Locale;
  onClose: () => void;
}) {
  const { ref, close } = useSlideScreen<HTMLDivElement>(onClose);
  // Portal to <body>: rendered inline, this `fixed` overlay would sit inside the
  // AppShell <main> (an overflow-y-auto scroll container), where iOS Safari
  // couples its scroll to the parent and overscroll chains into the page behind.
  return createPortal(
    <div ref={ref} className="fixed inset-0 z-[70] flex flex-col bg-[color:var(--canvas)] lg:hidden">
      <Panel storeSlug={storeSlug} customerId={customerId} locale={locale} onClose={close} />
    </div>,
    document.body,
  );
}

function Panel({
  storeSlug,
  customerId,
  locale,
  onClose,
}: {
  storeSlug: string;
  customerId: string;
  locale: Locale;
  onClose: () => void;
}) {
  const t = useTranslations();
  const { data: customer, isLoading, error } = useSellerCustomerQuery({ storeSlug, customerId });

  if (error && !isLoading) {
    return (
      <>
        <TopBar onClose={onClose} right={null} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <ErrorState message={t(keys.customers.loadError)} />
        </div>
      </>
    );
  }
  if (isLoading || !customer) {
    return (
      <>
        <TopBar onClose={onClose} right={null} />
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <LoadingSkeleton variant="card" rows={6} />
        </div>
      </>
    );
  }
  return (
    <LoadedScreen customer={customer} storeSlug={storeSlug} locale={locale} onClose={onClose} />
  );
}

function TopBar({ onClose, right }: { onClose: () => void; right: React.ReactNode }) {
  const t = useTranslations();
  return (
    <div
      className="flex shrink-0 items-center justify-between gap-2 px-3 pb-3"
      style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)" }}
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={t(keys.common.back)}
        className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
      >
        <BackIcon />
      </button>
      <div className="flex items-center gap-1.5">{right}</div>
    </div>
  );
}

function formatDate(value: string | null, locale: Locale): string {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(locale === "ar" ? "ar-EG" : "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US").format(value);
}

function LoadedScreen({
  customer,
  storeSlug,
  locale,
  onClose,
}: {
  customer: SellerCustomerDetailView;
  storeSlug: string;
  locale: Locale;
  onClose: () => void;
}) {
  const t = useTranslations();
  const { mutateAsync: updateCustomer, isPending } = useUpdateCustomerMutation();

  const [editing, setEditing] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [addingTag, setAddingTag] = useState(false);
  const [emailInput, setEmailInput] = useState<string | null>(null);

  const tint = tintFor(customer.name || "?");
  const gov = governorateName(customer, locale);
  const since = formatDate(customer.createdAt, locale);
  const lastOrder = formatDate(customer.lastOrderAt, locale);
  const avg =
    customer.ordersCount > 0 ? Math.round(customer.totalSpent / customer.ordersCount) : 0;
  const tags = customer.tags;
  const phoneDigits = customer.phoneE164.replace(/\D/g, "");
  const address = customer.lastAddress ?? gov ?? "";

  const commitTags = (next: string[]) => {
    void updateCustomer({ storeSlug, customerId: customer.id, tags: next }).catch(() => {});
  };
  const addTag = () => {
    const value = tagInput.trim();
    if (value && !tags.includes(value)) commitTags([...tags, value]);
    setTagInput("");
    setAddingTag(false);
  };
  const removeTag = (tag: string) => commitTags(tags.filter((x) => x !== tag));
  const saveEmail = () => {
    if (emailInput === null) return;
    const value = emailInput.trim();
    void updateCustomer({ storeSlug, customerId: customer.id, email: value || null }).catch(
      () => {},
    );
    setEmailInput(null);
  };
  const copyPhone = () => {
    void navigator.clipboard?.writeText(customer.phoneE164).catch(() => {});
  };

  return (
    <>
      <TopBar
        onClose={onClose}
        right={
          <>
            <button
              type="button"
              onClick={() => {
                if (editing) {
                  // Leaving edit mode — flush any open editors.
                  if (addingTag) addTag();
                  if (emailInput !== null) saveEmail();
                }
                setEditing((v) => !v);
              }}
              aria-label={t(keys.common.edit)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              style={editing ? { color: "var(--accent-500)" } : undefined}
            >
              {editing ? <CheckIcon /> : <EditIcon />}
            </button>
            <Menu as="div" className="relative flex">
              <MenuButton
                aria-label={t(keys.orders.mMore)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
              >
                <DotsIcon />
              </MenuButton>
              <MenuItems
                anchor="bottom end"
                className="z-[80] mt-1 w-[210px] rounded-[14px] border border-[color:var(--card-br)] bg-[color:var(--card)] p-1.5 shadow-[0_20px_54px_rgba(0,0,0,0.28)] focus:outline-none"
              >
                <MenuItem>
                  <button
                    type="button"
                    onClick={copyPhone}
                    className="flex w-full items-center gap-3 rounded-[10px] px-2.5 py-2.5 text-start text-[14px] font-medium text-[color:var(--text-primary)] data-[focus]:bg-[color:var(--soft)]"
                  >
                    <span className="text-[color:var(--text-muted)]"><CopyIcon /></span>
                    {t(keys.customers.copyPhone)}
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </>
        }
      />

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-[118px] pt-1">
        {/* Hero */}
        <div className="flex flex-col items-center text-center">
          <div className="relative">
            <span
              aria-hidden="true"
              className="flex h-[94px] w-[94px] items-center justify-center rounded-full text-[34px] font-extrabold tracking-[-0.02em]"
              style={{ background: tint.bg, color: tint.fg }}
            >
              {initialsOf(customer.name || "?")}
            </span>
            <span
              aria-hidden="true"
              className="absolute bottom-[5px] end-[3px] h-[19px] w-[19px] rounded-full border-[3px] border-[color:var(--canvas)]"
              style={{
                background:
                  customer.ordersCount > 0 ? "var(--success-500)" : "var(--text-disabled)",
              }}
            />
          </div>
          <h1 className="mt-3.5 text-[23px] font-extrabold tracking-[-0.02em] text-[color:var(--text-primary)]">
            {customer.name}
          </h1>
          {gov ? (
            <span
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-bold"
              style={{
                background: "color-mix(in srgb, var(--accent-500) 12%, transparent)",
                color: "var(--accent-500)",
              }}
            >
              <span
                aria-hidden="true"
                className="h-[7px] w-[7px] rounded-full"
                style={{ background: "var(--accent-500)" }}
              />
              {gov}
            </span>
          ) : null}
          {since ? (
            <div className="mt-2.5 text-[13px] font-medium text-[color:var(--text-muted)]">
              {t(keys.customers.sinceLabel, { date: since })}
            </div>
          ) : null}
          {lastOrder ? (
            <div className="mt-1 text-[12px] text-[color:var(--text-disabled)]">
              {t(keys.customers.tableLastOrder)}: {lastOrder}
            </div>
          ) : null}
        </div>

        {/* Stats */}
        <div className="mt-6 flex gap-2.5">
          <StatTile
            unit="EGP"
            value={formatNumber(customer.totalSpent, locale)}
            label={t(keys.customers.tableTotalSpent)}
          />
          <StatTile
            value={formatNumber(customer.ordersCount, locale)}
            label={t(keys.customers.tableOrders)}
          />
          <StatTile
            unit="EGP"
            value={formatNumber(avg, locale)}
            label={t(keys.customers.avgOrder)}
          />
        </div>

        {/* Contact */}
        <SectionLabel>{t(keys.customers.detailContactTitle)}</SectionLabel>
        <div className="overflow-hidden rounded-[16px] border border-[color:var(--hair)] bg-[color:var(--soft)] px-4">
          {/* Email — editable in edit mode */}
          <ContactRow icon={<EnvelopeIcon />}>
            {editing || emailInput !== null ? (
              <input
                autoFocus={emailInput !== null}
                type="email"
                value={emailInput ?? customer.email ?? ""}
                onChange={(e) => setEmailInput(e.target.value)}
                onFocus={() => {
                  if (emailInput === null) setEmailInput(customer.email ?? "");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEmail();
                }}
                onBlur={saveEmail}
                placeholder={t(keys.customers.mAddEmail)}
                className="w-full rounded-md border border-[color:var(--border-strong)] bg-[color:var(--canvas)] px-2 py-1 text-[13.5px] font-semibold text-[color:var(--text-primary)] outline-none"
              />
            ) : (
              <span className="truncate">
                {customer.email || (
                  <span className="font-medium text-[color:var(--text-muted)]">
                    {t(keys.customers.mAddEmail)}
                  </span>
                )}
              </span>
            )}
          </ContactRow>
          <ContactRow icon={<PhoneIcon />}>
            <bdi dir="ltr" className="truncate">{customer.phoneE164}</bdi>
          </ContactRow>
          {address ? (
            <ContactRow icon={<PinIcon />} last alignTop>
              <span className="line-clamp-2 leading-snug">{address}</span>
            </ContactRow>
          ) : null}
        </div>

        {/* Recent orders */}
        <div className="mb-2 mt-6 flex items-center gap-2">
          <span className="text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
            {t(keys.customers.recentOrders)}
          </span>
          <span className="text-[12px] font-semibold text-[color:var(--text-disabled)]">
            {t(keys.customers.ordersCountLabel, { count: customer.ordersCount })}
          </span>
        </div>
        {customer.orders.length === 0 ? (
          <div className="rounded-[16px] border border-[color:var(--hair)] bg-[color:var(--soft)] px-4 py-8 text-center text-[13px] text-[color:var(--text-muted)]">
            {t(keys.customers.noOrders)}
          </div>
        ) : (
          <div className="overflow-hidden rounded-[16px] border border-[color:var(--hair)] bg-[color:var(--soft)]">
            {customer.orders.map((order, i) => {
              const tone = DISPLAY_STATUS_TONE[toDisplayStatus(order.status as OrderStatus)];
              return (
                <div
                  key={order.id}
                  className={`flex items-center gap-3 px-4 py-3 ${
                    i === customer.orders.length - 1
                      ? ""
                      : "border-b border-[color:var(--hair)]"
                  }`}
                >
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ background: tone.dot }}
                  />
                  <span className="shrink-0 font-mono text-[13.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                    {order.orderCode}
                  </span>
                  <span className="truncate text-[12.5px] font-semibold text-[color:var(--text-muted)]">
                    {formatDate(order.createdAt, locale)}
                  </span>
                  <span className="flex-1" />
                  <span className="shrink-0 text-[13.5px] font-bold tracking-[-0.01em] text-[color:var(--text-primary)]">
                    <bdi>{formatEgp(order.totalAmount, locale)}</bdi>
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Tags */}
        <SectionLabel>{t(keys.customers.tagsTitle)}</SectionLabel>
        <div className="flex flex-wrap gap-[7px]">
          {tags.map((tag) => (
            <span
              key={tag}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[color:var(--hair)] bg-[color:var(--soft)] py-1.5 pe-1.5 ps-2.5 text-[12px] font-semibold text-[color:var(--text-secondary)]"
            >
              {tag}
              {editing ? (
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  disabled={isPending}
                  aria-label={`${t(keys.common.delete)} ${tag}`}
                  className="inline-flex h-4 w-4 items-center justify-center rounded text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--text-primary)] disabled:opacity-50"
                >
                  <CloseIcon />
                </button>
              ) : null}
            </span>
          ))}
          {editing ? (
            addingTag ? (
              <input
                autoFocus
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") addTag();
                  if (e.key === "Escape") {
                    setTagInput("");
                    setAddingTag(false);
                  }
                }}
                onBlur={addTag}
                placeholder={t(keys.customers.addTag)}
                className="w-[110px] rounded-lg border border-[color:var(--border-strong)] bg-[color:var(--canvas)] px-2.5 py-1.5 text-[12px] text-[color:var(--text-primary)] outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => setAddingTag(true)}
                disabled={isPending}
                className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-[color:var(--border-strong)] px-2.5 py-1.5 text-[12px] font-semibold text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--soft)] disabled:opacity-50"
              >
                <PlusIcon />
                {t(keys.customers.addTag)}
              </button>
            )
          ) : tags.length === 0 ? (
            <span className="text-[12.5px] text-[color:var(--text-muted)]">
              {t(keys.common.empty)}
            </span>
          ) : null}
        </div>
      </div>

      {/* Bottom action bar */}
      <div
        className="absolute bottom-0 start-0 end-0 flex items-center gap-2.5 px-5 pt-8"
        style={{
          paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 14px)",
          background:
            "linear-gradient(to top, var(--canvas) 55%, color-mix(in srgb, var(--canvas) 0%, transparent))",
        }}
      >
        <a
          href={`tel:${customer.phoneE164}`}
          aria-label={t(keys.customers.callAria)}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[15px] border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
        >
          <PhoneIcon />
        </a>
        <a
          href={`https://wa.me/${phoneDigits}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[15px] bg-[color:var(--accent-500)] px-4 text-[15px] font-semibold text-[color:var(--on-accent)] transition-opacity hover:opacity-90"
        >
          <ChatIcon />
          {t(keys.customers.message)}
        </a>
        <button
          type="button"
          onClick={copyPhone}
          aria-label={t(keys.customers.copyPhone)}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[15px] border border-[color:var(--card-br)] bg-[color:var(--soft2)] text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--soft)]"
        >
          <CopyIcon />
        </button>
      </div>
    </>
  );
}

// ─── Presentational bits ─────────────────────────────────────────────────────

/** Small "EGP" prefix + number, matching the design's currency stat tiles. */
function formatEgp(amount: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US", {
    style: "currency",
    currency: "EGP",
    maximumFractionDigits: 0,
  }).format(amount);
}

function StatTile({ unit, value, label }: { unit?: string; value: string; label: string }) {
  return (
    <div className="min-w-0 flex-1 rounded-[14px] bg-[color:var(--soft)] px-[11px] py-[13px]">
      <div className="flex items-baseline gap-[3px] whitespace-nowrap">
        {unit ? (
          <span className="text-[10.5px] font-bold text-[color:var(--text-muted)]">{unit}</span>
        ) : null}
        <span className="text-[18px] font-extrabold tracking-[-0.03em] text-[color:var(--text-primary)]">
          {value}
        </span>
      </div>
      <div className="mt-1 text-[11.5px] font-semibold text-[color:var(--text-muted)]">{label}</div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-2.5 mt-6 text-[12px] font-semibold uppercase tracking-[0.06em] text-[color:var(--text-muted)]">
      {children}
    </div>
  );
}

function ContactRow({
  icon,
  children,
  last,
  alignTop,
}: {
  icon: React.ReactNode;
  children: React.ReactNode;
  last?: boolean;
  /** Top-align the icon for multi-line values (e.g. a wrapping address). */
  alignTop?: boolean;
}) {
  return (
    <div
      className={`flex gap-3 py-3.5 ${alignTop ? "items-start" : "items-center"} ${
        last ? "" : "border-b border-[color:var(--hair)]"
      }`}
    >
      <span
        className={`shrink-0 text-[color:var(--text-muted)] ${alignTop ? "mt-px" : ""}`}
      >
        {icon}
      </span>
      <div className="min-w-0 flex-1 text-[13.5px] font-semibold tracking-[-0.01em] text-[color:var(--text-primary)]">
        {children}
      </div>
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const ICON = {
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor" as const,
  strokeWidth: 1.9,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
};

function BackIcon() {
  return (
    <svg {...ICON} width={18} height={18} strokeWidth={2} className="rtl:-scale-x-100">
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}
function EditIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg {...ICON} width={17} height={17} strokeWidth={2.4}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg {...ICON} width={16} height={16} stroke="none" fill="currentColor">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
function CopyIcon() {
  return (
    <svg {...ICON} width={16} height={16}>
      <rect x="9" y="9" width="12" height="12" rx="2.5" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg {...ICON} width={17} height={17} strokeWidth={2}>
      <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 21l1.9-5.7A8.5 8.5 0 0 1 4 11.5 8.38 8.38 0 0 1 12.5 3 8.38 8.38 0 0 1 21 11.5z" />
    </svg>
  );
}
function EnvelopeIcon() {
  return (
    <svg {...ICON} width={17} height={17} strokeWidth={1.8}>
      <rect x="3" y="5" width="18" height="14" rx="2.5" />
      <path d="m3.5 6.5 8.5 6 8.5-6" />
    </svg>
  );
}
function PhoneIcon() {
  return (
    <svg {...ICON} width={17} height={17} strokeWidth={1.8}>
      <path d="M4 5.5C4 4.7 4.7 4 5.5 4H8l1.5 4-2 1.5a11 11 0 0 0 5 5l1.5-2 4 1.5v2.5c0 .8-.7 1.5-1.5 1.5A15 15 0 0 1 4 5.5z" />
    </svg>
  );
}
function PinIcon() {
  return (
    <svg {...ICON} width={17} height={17} strokeWidth={1.8}>
      <path d="M12 21s7-5.7 7-11a7 7 0 1 0-14 0c0 5.3 7 11 7 11z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg {...ICON} width={13} height={13} strokeWidth={2}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg {...ICON} width={11} height={11} strokeWidth={2.2}>
      <path d="M6 6l12 12M6 18L18 6" />
    </svg>
  );
}
